import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AppLayout } from './components/Layout/AppLayout';
import { HomePage } from './pages/HomePage';
import { initialiseAnalytics, trackPageView } from './services/analytics';
import { findProviderBySlug, getProviderLink } from './utils/providerSlugs';

// Non-home pages are lazy-loaded so the initial bundle stays small.
// Each uses the named-export adapter pattern since pages export named functions.
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const ComparePage = lazy(() => import('./pages/ComparePage').then((m) => ({ default: m.ComparePage })));
const ChangesPage = lazy(() => import('./pages/ChangesPage').then((m) => ({ default: m.ChangesPage })));
const ChangesArchivePage = lazy(() => import('./pages/ChangesArchivePage').then((m) => ({ default: m.ChangesArchivePage })));
const ChangesSubscribePage = lazy(() => import('./pages/ChangesSubscribePage').then((m) => ({ default: m.ChangesSubscribePage })));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage').then((m) => ({ default: m.ChangelogPage })));
const IAMGovernancePage = lazy(() => import('./pages/IAMGovernancePage').then((m) => ({ default: m.IAMGovernancePage })));
const OpenAIResidencyPage = lazy(() => import('./pages/OpenAIResidencyPage').then((m) => ({ default: m.OpenAIResidencyPage })));
const AzureGovernancePage = lazy(() => import('./pages/AzureGovernancePage').then((m) => ({ default: m.AzureGovernancePage })));
const GCPGovernancePage = lazy(() => import('./pages/GCPGovernancePage').then((m) => ({ default: m.GCPGovernancePage })));
const ModelSupportPage = lazy(() => import('./pages/ModelSupportPage').then((m) => ({ default: m.ModelSupportPage })));
const ModelsIndexPage = lazy(() => import('./pages/ModelsIndexPage').then((m) => ({ default: m.ModelsIndexPage })));
const ModelDetailPage = lazy(() => import('./pages/ModelDetailPage').then((m) => ({ default: m.ModelDetailPage })));
const ModelComparePage = lazy(() => import('./pages/ModelComparePage').then((m) => ({ default: m.ModelComparePage })));
const ProvidersPage = lazy(() => import('./pages/ProvidersPage').then((m) => ({ default: m.ProvidersPage })));
const ProviderDetailPage = lazy(() => import('./pages/ProviderDetailPage').then((m) => ({ default: m.ProviderDetailPage })));
const CompliancePage = lazy(() => import('./pages/CompliancePage').then((m) => ({ default: m.CompliancePage })));
const ComplianceCertPage = lazy(() => import('./pages/ComplianceCertPage').then((m) => ({ default: m.ComplianceCertPage })));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage').then((m) => ({ default: m.SearchResultsPage })));
const ClaudeVsGPT4Page = lazy(() => import('./pages/comparisons/ClaudeVsGPT4Page').then((m) => ({ default: m.ClaudeVsGPT4Page })));
const GuidesIndexPage = lazy(() => import('./pages/growth/GuidesIndexPage').then((m) => ({ default: m.GuidesIndexPage })));
const DecisionGuidePage = lazy(() => import('./pages/growth/DecisionGuidePage').then((m) => ({ default: m.DecisionGuidePage })));
const StatusPage = lazy(() => import('./pages/StatusPage').then((m) => ({ default: m.StatusPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage').then((m) => ({ default: m.BlogIndexPage })));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));

function RouteAnalytics() {
  const location = useLocation();

  useEffect(() => {
    initialiseAnalytics();
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
}

// Redirect component for old provider/family URLs to new slug-based URLs
function ModelFamilyRedirect() {
  const { provider, family } = useParams<{ provider: string; family: string }>();

  // Convert old format to new slug format
  if (family) {
    // Try to construct a reasonable slug from the family key
    // Replace dashes with dots for version numbers
    const slug = family.replace(/-(\d+)-(\d+)$/, '-$1.$2');
    const target = `/models/${encodeURIComponent(slug)}`;
    trackPageView(`/redirect/models/${provider}/${family}`);
    return <Navigate to={target} replace />;
  }

  trackPageView('/redirect/models/unknown');
  return <Navigate to="/models" replace />;
}

// Redirect old provider URLs to the canonical provider slug.
function ProviderRedirect() {
  const { provider } = useParams<{ provider: string }>();

  if (provider) {
    try {
      const decoded = decodeURIComponent(provider);
      // Try to find the canonical provider name and get the correct link
      const providerName = findProviderBySlug(decoded);
      const target = providerName ? getProviderLink(providerName) : getProviderLink(decoded);
      trackPageView(`/redirect/provider/${decoded}`);
      return <Navigate to={target} replace />;
    } catch {
      trackPageView('/redirect/provider/error');
      return <Navigate to="/providers" replace />;
    }
  }

  trackPageView('/redirect/provider/empty');
  return <Navigate to="/providers" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <RouteAnalytics />
      <Suspense fallback={null}>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/about" element={<AboutPage />} />
            
            {/* NEW: Model-first routes */}
            <Route path="/models" element={<ModelsIndexPage />} />
            <Route path="/models/all" element={<ModelSupportPage />} />
            
            {/* NEW: Unified model detail page (model-first) */}
            <Route path="/models/:modelKey" element={<ModelDetailPage />} />
            
            {/* DEPRECATED: Old provider/family URLs - redirect to new format */}
            <Route path="/models/:provider/:family" element={<ModelFamilyRedirect />} />
            
            <Route path="/providers" element={<ProvidersPage />} />
            <Route path="/providers/:provider" element={<ProviderDetailPage />} />
            
            {/* Catch old provider URLs with spaces/encoding and redirect to slug format */}
            <Route 
              path="/provider/:provider" 
              element={<ProviderRedirect />} 
            />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/compliance/:certification" element={<ComplianceCertPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/compare/models" element={<ModelComparePage />} />
            <Route path="/compare/claude-vs-gpt4" element={<ClaudeVsGPT4Page />} />
            <Route path="/compare/bedrock-vs-anthropic" element={<DecisionGuidePage />} />
            <Route path="/compare/openai-vs-azure-openai-residency" element={<DecisionGuidePage />} />
            <Route path="/guides" element={<GuidesIndexPage />} />
            <Route path="/guides/:guideSlug" element={<DecisionGuidePage />} />
            <Route path="/governance" element={<Navigate to="/governance/aws" replace />} />
            <Route path="/governance/aws" element={<IAMGovernancePage />} />
            <Route path="/governance/openai" element={<OpenAIResidencyPage />} />
            <Route path="/governance/azure" element={<AzureGovernancePage />} />
            <Route path="/governance/gcp" element={<GCPGovernancePage />} />
            <Route path="/changes" element={<ChangesPage />} />
            <Route path="/changes/archive" element={<ChangesArchivePage />} />
            <Route path="/changes/subscribe" element={<ChangesSubscribePage />} />
            <Route path="/changes/:issueDate" element={<ChangesPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/openai-residency" element={<Navigate to="/governance/openai" replace />} />
            <Route path="/iam-governance" element={<Navigate to="/governance/aws" replace />} />
            <Route path="/profile/:profileId" element={<ProfilePage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            {/* Blog routes */}
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
